import React, { useEffect, useRef, useState } from 'react';

// Defers loading of below-the-fold autoplay videos until they approach
// the viewport, so the homepage only downloads the hero video up front.
// Once the source attaches, playback must be started explicitly —
// browsers do not honor the autoplay attribute for late-added sources.
const LazyVideo = ({ src, poster, className, style, rootMargin = '400px' }) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;
    if (typeof IntersectionObserver === 'undefined') {
      setVisible(true);
      return undefined;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [rootMargin]);

  useEffect(() => {
    if (!visible || !ref.current) return;
    const video = ref.current;
    // The muted attribute is required for autoplay policy, and calling
    // play() immediately is what triggers Chrome to start loading at all.
    video.muted = true;
    video.setAttribute('muted', '');
    const attempt = video.play();
    if (attempt && attempt.catch) attempt.catch(() => {});
  }, [visible]);

  return (
    <video
      ref={ref}
      src={visible ? src : undefined}
      poster={poster}
      className={className}
      style={style}
      autoPlay
      muted
      loop
      playsInline
      preload={visible ? 'auto' : 'none'}
      aria-hidden="true"
    />
  );
};

export default LazyVideo;
